import React from 'react'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { HiddenAccountVO } from '../../../apis/types'
import { formatReadableDate } from '../../../util/string'

interface HiddenAccountsPanelProps {
    accounts: HiddenAccountVO[]
}

const HiddenAccountsPanel: React.FC<HiddenAccountsPanelProps> = ({ accounts }) => {
    if (accounts.length === 0) return null

    return (
        <div className="mt-4">
            <Disclosure defaultOpen={false}>
                {({ open }) => (
                    <>
                        <DisclosureButton className="flex w-full items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 focus:outline-none">
                            <span>
                                Intentionally Excluded Accounts{' '}
                                <span className="inline-flex items-center ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                                    {accounts.length}
                                </span>
                            </span>
                            <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </DisclosureButton>
                        <DisclosurePanel className="border border-t-0 border-slate-200 dark:border-slate-700 rounded-b-lg">
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-800 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            <th className="px-4 py-2">Account Number</th>
                                            <th className="px-4 py-2">Account Name</th>
                                            <th className="px-4 py-2">Service Address</th>
                                            <th className="px-4 py-2">Tariff</th>
                                            <th className="px-4 py-2">Last Bill Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {accounts.map((acct, i) => (
                                            <tr
                                                key={acct.accountNumber}
                                                className={i % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'}
                                            >
                                                <td className="px-4 py-2 font-mono text-slate-900 dark:text-slate-100">{acct.accountNumber}</td>
                                                <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{acct.accountName || '—'}</td>
                                                <td className="px-4 py-2 text-slate-700 dark:text-slate-300">
                                                    {acct.services.length > 0 ? acct.services[0].serviceAddress || '—' : '—'}
                                                    {acct.services.length > 1 && (
                                                        <span className="ml-1 text-xs text-slate-400">+{acct.services.length - 1} more</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-slate-700 dark:text-slate-300">
                                                    {acct.services.length > 0 ? acct.services[0].tariff || '—' : '—'}
                                                </td>
                                                <td className="px-4 py-2 text-slate-700 dark:text-slate-300">{formatReadableDate(acct.lastBillDate) || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </DisclosurePanel>
                    </>
                )}
            </Disclosure>
        </div>
    )
}

export default HiddenAccountsPanel
